import { supabase } from '../lib/supabase';
import { VideoProcessingJob } from './videoProcessingJobsService';

export interface JobMonitor {
  videoId: string;
  jobs: VideoProcessingJob[];
  onStatusChange?: (videoId: string, jobs: VideoProcessingJob[]) => void;
}

/**
 * Service for monitoring video processing job statuses
 * Polls job statuses and notifies on changes
 */
export const jobMonitoringService = {
  /**
   * Start monitoring jobs for a video
   * Returns a cleanup function to stop monitoring
   */
  startMonitoring: (
    videoId: string,
    onStatusChange: (jobs: VideoProcessingJob[]) => void,
    pollInterval: number = 5000 // Poll every 5 seconds
  ): (() => void) => {
    let isActive = true;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (!isActive) return;

      try {
        const { data, error } = await supabase
          .from('video_processing_jobs')
          .select('*')
          .eq('video_id', videoId)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (!error && data) {
          onStatusChange(data);
        }

        // Continue polling if there are queued or running jobs
        const hasActiveJobs = data?.some(
          (job) => job.status === 'queued' || job.status === 'running'
        );

        if (hasActiveJobs && isActive) {
          pollTimer = setTimeout(poll, pollInterval);
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        if (isActive) {
          pollTimer = setTimeout(poll, pollInterval);
        }
      }
    };

    // Start polling immediately
    poll();

    // Return cleanup function
    return () => {
      isActive = false;
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
    };
  },

  /**
   * Monitor multiple videos at once
   */
  startBatchMonitoring: (
    videoIds: string[],
    onStatusChange: (videoId: string, jobs: VideoProcessingJob[]) => void,
    pollInterval: number = 5000
  ): (() => void) => {
    const cleanupFunctions: (() => void)[] = [];

    videoIds.forEach((videoId) => {
      const cleanup = jobMonitoringService.startMonitoring(
        videoId,
        (jobs) => onStatusChange(videoId, jobs),
        pollInterval
      );
      cleanupFunctions.push(cleanup);
    });

    // Return combined cleanup function
    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  },

  /**
   * Check if all jobs for a video are complete
   */
  areAllJobsComplete: (jobs: VideoProcessingJob[]): boolean => {
    if (jobs.length === 0) return true;
    return jobs.every(
      (job) => job.status === 'succeeded' || job.status === 'failed'
    );
  },

  /**
   * Check if any job failed
   */
  hasAnyJobFailed: (jobs: VideoProcessingJob[]): boolean => {
    return jobs.some((job) => job.status === 'failed');
  },

  /**
   * Get job summary for display
   */
  getJobSummary: (jobs: VideoProcessingJob[]): {
    total: number;
    queued: number;
    running: number;
    succeeded: number;
    failed: number;
  } => {
    return {
      total: jobs.length,
      queued: jobs.filter((j) => j.status === 'queued').length,
      running: jobs.filter((j) => j.status === 'running').length,
      succeeded: jobs.filter((j) => j.status === 'succeeded').length,
      failed: jobs.filter((j) => j.status === 'failed').length,
    };
  },
};

